import type {
  ScheduleListItem,
  SchedulesInConflicts,
} from 'lib-shared-types-api';
import type { PropsWithChildren } from 'lib-shared-types-utils';
import noop from 'lodash/noop';
import { createContext, useContext } from 'react';

import { useSchedules } from './utils/useSchedules';

type OpeningHoursProviderProps = PropsWithChildren;

/** Represents the shape of the context value. */
export type OpeningHoursContextType = {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  isLoading: boolean;
  schedules: readonly ScheduleListItem[];
  schedulesInConflicts: readonly SchedulesInConflicts[];
  totalNumberOfSchedules: number;
  nextPage: VoidFunction;
  previousPage: VoidFunction;
  refetchItems: VoidFunction;
  refetchSchedulesInConflicts: VoidFunction;
};

/** The context. */
export const OpeningHoursContext = createContext<OpeningHoursContextType>({
  hasNextPage: false,
  hasPreviousPage: false,
  isLoading: false,
  schedules: [],
  schedulesInConflicts: [],
  totalNumberOfSchedules: 0,
  nextPage: noop,
  previousPage: noop,
  refetchItems: noop,
  refetchSchedulesInConflicts: noop,
});

/** The context provider. All its children will have access to its value. */
export const OpeningHoursProvider = ({
  children,
}: OpeningHoursProviderProps): JSX.Element => {
  const {
    asyncStatus,
    currentPageSchedules,
    hasNextPage,
    hasPreviousPage,
    schedulesInConflicts,
    totalNumberOfSchedules,
    nextPage,
    previousPage,
    refetchItems,
    refetchSchedulesInConflicts,
  } = useSchedules();

  return (
    <OpeningHoursContext.Provider
      value={{
        hasNextPage,
        hasPreviousPage,
        isLoading: asyncStatus === 'pending',
        schedules: currentPageSchedules,
        schedulesInConflicts,
        totalNumberOfSchedules,
        nextPage,
        previousPage,
        refetchItems,
        refetchSchedulesInConflicts,
      }}
    >
      {children}
    </OpeningHoursContext.Provider>
  );
};

/** A hook exposing the context functionality. */
export const useOpeningHoursContext = (): OpeningHoursContextType => {
  const context = useContext(OpeningHoursContext);

  if (!context) {
    throw new Error('Missing OpeningHoursContext');
  }

  return context;
};
